import { useEffect } from 'react';
import { useAuthStore } from '@/lib/stores/auth';
import { clearGleapIdentity, identifyAnonymousGleapVisitor, identifyGleapUser } from '@/lib/gleap';
import { useProfileQuery } from '@/features/settings/queries';
import { useSubscription } from '@/features/billing/queries';
import { useAnonymousGleapHashQuery, useGleapUserHashQuery } from './gleap-queries';

/**
 * Keeps the Gleap widget's identity in sync with the app's auth state —
 * mounted once, app-wide, in `main.tsx`. Without this, Gleap keeps whatever
 * identity it last held (or its own default per-browser visitor id, if never
 * identified at all), so support tickets/conversations stay visible across
 * logout, across a different user signing into the same browser, and even
 * before any login — the account-confusion bug this component exists to fix.
 *
 * Reactive on the store's `user` (not a one-shot boot check), so it re-syncs
 * immediately on login/logout with no page reload, and — because it reads
 * through the `useAuthStore` hook rather than a boot-time `getState()`
 * snapshot — it naturally waits for the persisted session to rehydrate
 * before making its first call.
 *
 * Waits for a backend-signed hash (`gleap-queries.ts`) before calling
 * `identify()` at all — this Gleap project's dashboard has Secure Mode on,
 * so an unsigned call is rejected with a 401 and never takes effect (worse
 * than not calling it: it's silent, so the stale identity looks live).
 */
export function GleapIdentitySync() {
  const user = useAuthStore((s) => s.user);
  const userHash = useGleapUserHashQuery(user?.id);
  const anonymousHash = useAnonymousGleapHashQuery(!user);
  // Only `GET /profile` carries the phone, so it can land after the identify
  // below. In `deps` for that reason — a late arrival re-identifies rather than
  // leaving the ticket without a number.
  const phoneNumber = useProfileQuery({ enabled: Boolean(user) }).data?.phoneNumber ?? null;
  // Plan + Paddle customer for the support links. `GET /billing/subscription`
  // is readable by any workspace member (see `useSubscription`), and shares the
  // cache entry the billing surfaces already use. Same late-arrival rule as the
  // phone: in `deps`, so a late answer re-identifies.
  const subscription = useSubscription({ enabled: Boolean(user) }).data;
  const plan = subscription?.plan.name ?? null;
  const paddleCustomerId = subscription?.paddleCustomerId ?? null;
  const organizationId = useAuthStore((s) => s.organization?.id ?? null);

  useEffect(() => {
    if (user && userHash.data?.hash) {
      clearGleapIdentity();
      identifyGleapUser(user, userHash.data.hash, { phone: phoneNumber, plan, organizationId, paddleCustomerId });
    } else if (!user && anonymousHash.data?.anonymousUserId && anonymousHash.data.anonymousUserHash) {
      clearGleapIdentity();
      identifyAnonymousGleapVisitor(anonymousHash.data.anonymousUserId, anonymousHash.data.anonymousUserHash);
    }
  }, [user, userHash.data, anonymousHash.data, phoneNumber, plan, organizationId, paddleCustomerId]);

  return null;
}
